import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BookOpen,
  Bookmark,
  Bot,
  Building2,
  Calendar,
  CalendarClock,
  CalendarRange,
  Flame,
  Gamepad2,
  ArrowLeftRight,
  Heart,
  HeartPulse,
  Home,
  Link2,
  Mail,
  Plug,
  Search,
  Tag,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";

export type NavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  badge?: "compare";
  /** When true, only renders if useUser().email is in ADMIN_EMAILS. */
  adminOnly?: boolean;
  /** When true, only renders if useUser().email is in SUPER_ADMIN_EMAILS. */
  superAdminOnly?: boolean;
  /** Visible for paid plans (pro/studio) and admins. */
  paidOnly?: boolean;
  /** Visible with API access: Studio and admins (hasApiAccess). */
  apiOnly?: boolean;
};

export type NavGroup = {
  title: string;
  items: NavItem[];
  /**
   * Hides the WHOLE group from non-superadmins, whatever its items say — so an
   * item added here later without its own flag still can't leak.
   */
  superAdminOnly?: boolean;
  /** Hides the whole group from viewers without API access, like superAdminOnly. */
  apiOnly?: boolean;
};

export const NAV_GROUPS: NavGroup[] = [
  // Admin-only section, rendered at the very top. The whole group is hidden for
  // non-admins (getVisibleNavGroups drops groups whose items all filter out).
  {
    title: "Admin",
    items: [
      { href: "/admin/workers", label: "Worker Health", icon: HeartPulse, adminOnly: true },
      { href: "/admin/newtrending", label: "New & Trending", icon: Flame, adminOnly: true },
    ],
  },
  // Superadmin tools. Gated at the group AND on every item, like Marketing.
  {
    title: "Superadmins",
    superAdminOnly: true,
    items: [
      { href: "/admin/subscriptions", label: "Subscriptions", icon: UserCog, superAdminOnly: true },
    ],
  },
  // The API's own menu, for Studio and admins: the dashboard (keys, usage),
  // agent setup and the reference. Gated at the group AND on every item, like
  // Marketing.
  {
    title: "API",
    apiOnly: true,
    items: [
      { href: "/agentic", label: "API dashboard", icon: Bot, apiOnly: true },
      { href: "/agentic#connect", label: "Connect an AI agent", icon: Plug, apiOnly: true },
      { href: "/docs/api", label: "API reference", icon: BookOpen, apiOnly: true },
    ],
  },
  {
    title: "Main",
    items: [
      { href: "/", label: "Home", icon: Home },
      { href: "/search", label: "Search", icon: Search },
      { href: "/all-games", label: "All Games", icon: Gamepad2 },
      { href: "/watchlist", label: "Watchlist", icon: Bookmark },
      // Public on purpose: Creem's account review wants pricing "easy to
      // find", and the footer lists everything in PUBLIC_NAV_ITEMS.
      { href: "/pricing", label: "Pricing", icon: Tag },
    ],
  },
  {
    title: "Rankings",
    items: [
      { href: "/topsellers", label: "Top Sellers", icon: TrendingUp },
      { href: "/topwishlist", label: "Top Wishlist", icon: Heart },
      { href: "/topfollowers", label: "Top Followers", icon: Users },
      { href: "/topccu", label: "Current Online", icon: Activity },
      { href: "/topmlt", label: "More Like This", icon: Link2 },
    ],
  },
  {
    title: "Analytics",
    items: [
      { href: "/releases", label: "Releases", icon: Calendar },
      // Public since 2026-10: the next 7 days for everyone, the full calendar is Pro.
      { href: "/upcoming-releases", label: "Upcoming Releases", icon: CalendarClock },
      { href: "/prerelease", label: "Pre-release", icon: CalendarRange },
      { href: "/publishers", label: "Publishers", icon: Building2 },
      { href: "/compare", label: "Compare", icon: ArrowLeftRight, badge: "compare" },
    ],
  },
  // Superadmin-only. Gated at the group AND on every item (defense in depth).
  // One entry by decision (2026-09-25): the hub (/admin/marketing) and the
  // landing preview (/landing4) still exist, reachable by URL, not from here.
  {
    title: "Marketing",
    superAdminOnly: true,
    items: [
      { href: "/admin/marketing/mail", label: "Outreach Mail", icon: Mail, superAdminOnly: true },
    ],
  },
];

/** EVERY item, gated ones included — never render it for everyone; see PUBLIC_NAV_ITEMS. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

export type NavViewer = {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isPaid: boolean;
  /** Studio or admin (hasApiAccess). Absent reads as false. */
  hasApi?: boolean;
};

/**
 * The drawer's visibility rule. Groups left with no visible items are dropped,
 * so the Admin/Superadmins/Marketing headings vanish together with their links.
 */
export function getVisibleNavGroups(
  viewer: NavViewer,
  groups: NavGroup[] = NAV_GROUPS,
): NavGroup[] {
  const { isAdmin, isSuperAdmin, isPaid } = viewer;
  const hasApi = viewer.hasApi === true;
  return groups
    .filter((group) => (!group.superAdminOnly || isSuperAdmin) && (!group.apiOnly || hasApi))
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (!item.adminOnly || isAdmin) &&
          (!item.superAdminOnly || isSuperAdmin) &&
          (!item.paidOnly || isPaid || isAdmin) &&
          (!item.apiOnly || hasApi),
      ),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Links safe to render for EVERY visitor (the footer): no adminOnly,
 * superAdminOnly or apiOnly item, and nothing from a superAdminOnly or apiOnly
 * group. paidOnly items stay — their pages gate themselves, and the link
 * doubles as an upsell.
 */
export function getPublicNavItems(groups: NavGroup[] = NAV_GROUPS): NavItem[] {
  return groups
    .filter((group) => !group.superAdminOnly && !group.apiOnly)
    .flatMap((group) => group.items)
    .filter((item) => !item.adminOnly && !item.superAdminOnly && !item.apiOnly);
}

export const PUBLIC_NAV_ITEMS: NavItem[] = getPublicNavItems();
